import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { setToken } from "@/lib/api";
import api from "@/lib/api";

/**
 * Inbound SSO landing page.
 * The Events app redirects here with a single-use hand-off token in the URL
 * FRAGMENT (#t=…) — a fragment is never sent to any server, so the token cannot
 * leak into access logs or a Referer header.
 * We hand it to our own backend, which verifies + consumes it server-to-server
 * with the issuing app, then returns a normal Solution session.
 */
export default function SsoLanding() {
  const nav = useNavigate();
  const [error, setError] = useState("");

  useEffect(() => {
    const run = async () => {
      // accept #t=… ; also tolerate ?t=… for older links
      const frag = new URLSearchParams((window.location.hash || "").replace(/^#/, ""));
      const qs = new URLSearchParams(window.location.search);
      const token = frag.get("t") || qs.get("t");

      // Remove the token from the address bar immediately, whatever happens next.
      window.history.replaceState({}, "", "/sso");

      if (!token) { setError("No sign-in token was provided."); return; }
      try {
        const res = await api.ssoAccept(token);
        if (!res?.token) throw new Error("Sign-in failed.");
        setToken(res.token);
        const me = await api.me().catch(() => null);
        nav(me?.user ? "/portal" : "/", { replace: true });
      } catch (e) {
        setError(e.message || "That sign-in link is invalid or has expired.");
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen grid place-items-center px-6">
      <div className="glass rounded-2xl p-8 max-w-md w-full text-center">
        {!error ? (
          <>
            <div className="font-display text-2xl font-extrabold tracking-tighter">Signing you in…</div>
            <p className="text-sm text-muted-foreground mt-2">One moment while we verify your session.</p>
          </>
        ) : (
          <>
            <div className="font-display text-2xl font-extrabold tracking-tighter">Couldn&apos;t sign you in</div>
            <p className="text-sm text-muted-foreground mt-2">{error}</p>
            <p className="text-xs text-muted-foreground mt-3">
              Hand-off links are valid for 60 seconds and can only be used once.
            </p>
            <button onClick={() => nav("/login", { replace: true })}
              className="mt-5 rounded-full bg-cobalt-500 hover:bg-cobalt-600 text-white px-5 py-2 text-sm font-medium">
              Go to sign in
            </button>
          </>
        )}
      </div>
    </div>
  );
}
